import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ContractDto } from 'src/generated/services/TFinancialApi';
import { ContractManagerShippingService } from './ShippingService';
import { ClipboardProvider } from './rateCardCharges/ClipboardProvider.tsx';
import { castToModality, Modality } from '../../../../types.ts';
import { ContractManagerRateCardCharges } from './RateCardCharges.tsx';
import { useTourContext } from 'src/presentations/guided-tours';
import clsx from 'clsx';

export const ContractManagerMain = () => {
  const { appTour } = useTourContext();
  appTour.registerTour('rate-card-table');
  const [searchParams] = useSearchParams();
  const [serviceProvider, setServiceProvider] = useState<string>(null);
  const [urlShippingService, setUrlShippingService] = useState('');
  const [shippingService, setShippingService] = useState('');
  const [chargeCategory, setChargeCategory] = useState('declarationCharges');
  const [contract, setContract] = useState<ContractDto>(null);
  const [modality, setModality] = useState<Modality>(null);

  // Initialize parameters from URL if they exist
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      // The actual validation and setting will happen in the ChargeCategory component
      // This is just to ensure we have the initial state set correctly
      setChargeCategory((prev) => prev); // Trigger a re-render
    }

    const serviceParam = searchParams.get('shipping_service');
    if (serviceParam) {
      setUrlShippingService(serviceParam);
    }

    const serviceProviderParam = searchParams.get('service-provider');
    if (serviceProviderParam && serviceProvider === null) {
      setServiceProvider(serviceProviderParam);
    }

    // Get the contract ID from URL to pass to RateCardCharges
    const contractParam = searchParams.get('contract');
    if (contractParam) {
      // We pass the contract ID to the RateCardCharges component
      // The actual contract object will be set in the RateCardCharges component
      // when the contractList is loaded
    }

    // Get the modality from URL to pass to RateCardCharges
    const modalityParam = searchParams.get('modality');
    if (modalityParam) {
      setModality(castToModality(modalityParam));
    }
  }, [searchParams, serviceProvider]);

  // Animation references and state
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [animationState, setAnimationState] = useState<'enter' | 'exit' | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [tableResized, setTableResized] = useState(false);

  // Drag-to-resize state for the CHARGES DETAILS panel
  const [panelTopVh, setPanelTopVh] = useState(54);
  const isDraggingRef = useRef(false);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    e.preventDefault();

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const vh = (moveEvent.clientY / window.innerHeight) * 100;
      setPanelTopVh(Math.min(Math.max(vh, 20), 80));
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  // CSS for the animation
  const animationStyles = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(100px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeOutDown {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(100px);
      }
    }

    .slide-container-enter {
      animation: fadeInUp 0.4s ease forwards;
    }

    .slide-container-exit {
      animation: fadeOutDown 0.4s ease forwards;
    }
  `;

  // Handle entrance and exit animations
  useEffect(() => {
    if (shippingService) {
      setAnimationState('enter'); // This was missing!
      setShowContent(true);
      setTableResized(true);

      // Allow a small delay for the drawer to start showing
      setTimeout(() => {
        // Find the selected row by attribute
        const selectedRow = document.querySelector('[data-row-selected="true"]');

        if (selectedRow) {
          // Simple scroll to center the selected row smoothly
          selectedRow.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 300);
    } else if (showContent) {
      setAnimationState('exit');
      setTableResized(false);
      setTimeout(() => {
        setShowContent(false);
      }, 500);
    }
  }, [shippingService, showContent]);

  return (
    <ClipboardProvider>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        ${animationStyles}
        
        /* Styling for table container with animation */
        .shipping-service-container {
          height: ${tableResized ? `${panelTopVh}vh` : '100%'};
          transition: ${isDraggingRef.current ? 'none' : 'height 0.4s ease-in-out'};
          overflow: hidden;
        }
        
        /* Ensure pagination stays visible and maintains its size */
        .MuiTablePagination-root {
          position: sticky;
          bottom: 0;
          background-color: white;
          z-index: 10;
          border-top: 1px solid #e5e7eb;
          min-height: 64px;
        }
        
        /* Keep pagination container fixed */
        .shipping-service-pagination {
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 10;
          min-height: 70px;
          flex-shrink: 0;
          width: 100%;
        }
      `,
        }}
      />

      <div className='w-full h-full relative overflow-hidden'>
        {/* Main content wrapper to establish common width constraints */}
        <div className='h-full flex flex-col mx-auto w-full max-w-full'>
          {/* Table container with animation */}
          <div ref={tableContainerRef} className='shipping-service-container'>
            <ContractManagerShippingService
              urlShippingService={urlShippingService}
              serviceProvider={serviceProvider}
              setModality={setModality}
              shippingServiceId={shippingService}
              setShippingServiceId={setShippingService}
              setContract={setContract}
              modality={modality}
            />
          </div>

          {/* Drawer container - Always render for tour steps, but hide when not in use */}
          <div
            ref={containerRef}
            className={clsx(
              'absolute z-[10] bottom-0 bg-white w-full border-t border-neutral-20 shadow-[0_-4px_12px_0_rgba(0,0,0,0.1)]',
              !showContent && 'hidden',
              showContent && animationState === 'enter' && 'slide-container-enter',
              showContent && animationState === 'exit' && 'slide-container-exit'
            )}
            style={{ top: showContent ? `${panelTopVh}vh` : '100%' }}
          >
            {/* Drag handle */}
            <div
              className='h-5 flex items-center justify-center cursor-row-resize bg-white hover:bg-neutral-10 select-none flex-shrink-0'
              onMouseDown={handleDragStart}
            >
              <div className='w-10 h-1 rounded-full bg-neutral-30' />
            </div>
            <ContractManagerRateCardCharges
              shippingServiceId={shippingService}
              setShippingServiceId={setShippingService}
              chargeCategory={chargeCategory}
              contract={contract}
              setContract={setContract}
              contractId={searchParams.get('contract')}
              modality={modality}
            />
          </div>
        </div>
      </div>
    </ClipboardProvider>
  );
};
